import * as React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Card, Typography, FormControlLabel, Switch, IconButton, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { VideoModal } from '../components/VideoModal';
import { AnimatedItem } from '../components/animations/AnimatedItem';
import { FeatureNotesDialog } from './dialogs/FeatureNotesDialog';
import { FeatureNotesPreviewDialog } from './dialogs/FeatureNotesPreviewDialog';

const ICON_CHIP_BG = 'rgba(81, 115, 255, 0.1)';
const MAX_ANIMATION_DELAY = 300;

// Shared with FeatureLayoutCardClone so the drag preview is pixel-identical to the card it lifts.
export const useFeatureLayoutCardStyles = makeStyles((theme: Theme) =>
    createStyles({
        cardSlot: {
            width: '100%',
            height: '100%',
            display: 'flex',
            padding: theme.spacing(1),
            boxSizing: 'border-box',
        },
        grabCursor: {
            cursor: 'grab',
        },
        pointerCursor: {
            cursor: 'pointer',
        },
        animatedFill: {
            display: 'flex',
            flex: 1,
            minWidth: 0,
        },
        card: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
            borderRadius: 8,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            backgroundColor: theme.palette.background.paper,
            transition: 'box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                boxShadow: '0 4px 18px rgba(0,0,0,0.10)',
                borderColor: theme.palette.primary.light,
                transform: 'translateY(-1px)',
            },
            '@media (prefers-reduced-motion: reduce)': {
                '&:hover': {
                    transform: 'none',
                },
            },
        },
        // Explicit request: a hidden chart was too close to a visible one (a very light grey on white). A clearly darker grey card and a darker
        // dashed border make it obvious at a glance which charts are switched off.
        cardHidden: {
            backgroundColor: theme.palette.grey[300],
            borderStyle: 'dashed',
            borderColor: theme.palette.grey[500],
            '& $title': {
                color: theme.palette.text.secondary,
            },
            '& $iconChip': {
                backgroundColor: theme.palette.grey[400],
                color: theme.palette.grey[700],
            },
        },
        cardBody: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
            padding: theme.spacing(1.5, 1.5, 1, 1.5),
            flex: 1,
        },
        iconChip: {
            width: 40,
            height: 40,
            borderRadius: 10,
            backgroundColor: ICON_CHIP_BG,
            color: theme.palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': {
                fontSize: 22,
            },
        },
        title: {
            flex: 1,
            minWidth: 0,
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.4,
            color: theme.palette.text.primary,
            wordBreak: 'break-word',
        },
        dragIndicator: {
            color: theme.palette.text.disabled,
            fontSize: 18,
            flexShrink: 0,
            alignSelf: 'flex-start',
        },
        cardFooter: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(0, 1, 0.75, 1.5),
            borderTop: `1px solid ${theme.palette.divider}`,
            minHeight: 40,
        },
        visibilityLabel: {
            marginLeft: -6,
            marginRight: 0,
            '& .MuiFormControlLabel-label': {
                fontSize: 12,
                color: theme.palette.text.secondary,
            },
        },
        footerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
        },
        footerButton: {
            color: theme.palette.primary.main,
            '& svg': {
                fontSize: 18,
            },
        },
    })
);

interface FeatureLayoutCardContentProps {
    featureGuid: string;
    feature: string;
    title: string;
    description: string;
    isHidden: boolean;
    onIsHiddenUpdated: (featureGuid: string, isHidden: boolean) => void;
    isDragDisabled?: boolean;
    infoUrl: string | null;
    isInternalAdmin: boolean;
    /** Entrance animation delay (ms) — capped so long lists don't trail in for seconds */
    animationDelay?: number;
}

// Explicit bug report: dragging still felt laggy even after memoizing the old single component.
// Root cause: useSortable() (below) subscribes directly to @dnd-kit's drag-state context, so
// whichever component CALLS that hook re-renders on every drag frame regardless of React.memo -
// memoizing a component doesn't stop it re-rendering because of its OWN hook's subscription,
// only because of unchanged props from a parent. Splitting the heavy visual content (this
// component - Card/Switch/Tooltips/dialogs/AnimatedItem) out from the thin useSortable wrapper
// below means this half only re-renders for its own state changes or genuinely new business
// props (title/isHidden/etc) - never just because some other card's drag position shifted.
const FeatureLayoutCardContentComponent: React.FC<FeatureLayoutCardContentProps> = props => {
    const classes = useFeatureLayoutCardStyles();

    const [isVisible, setIsVisible] = React.useState<boolean>(!props.isHidden);
    const [isVideoModalOpen, setIsVideoModalOpen] = React.useState<boolean>(false);
    const [isEditNotesOpen, setIsEditNotesOpen] = React.useState<boolean>(false);
    const [isNotesPreviewOpen, setIsNotesPreviewOpen] = React.useState<boolean>(false);

    // Visibility is flipped in place on the shared feature object (e.g. by Show All / Hide All),
    // so re-sync whenever the incoming flag changes — same as the old componentWillReceiveProps.
    React.useEffect(() => {
        setIsVisible(!props.isHidden);
    // tslint:disable-next-line: align
    }, [props.isHidden]);

    const onHiddenChange = () => {
        setIsVisible(!isVisible);

        if (props.onIsHiddenUpdated) {
            props.onIsHiddenUpdated(props.featureGuid, !isVisible);
        }
    };

    const onToggleNotesPreview = () => {
        setIsNotesPreviewOpen(!isNotesPreviewOpen);
    };

    return (
        <React.Fragment>
            {isEditNotesOpen ? (
                <FeatureNotesDialog
                    open={isEditNotesOpen}
                    description={props.description}
                    title={props.title}
                    featureGuid={props.featureGuid}
                    feature={props.feature}
                    infoUrl={props.infoUrl}
                    onClose={() => setIsEditNotesOpen(false)}
                />
            ) : undefined}

            {isNotesPreviewOpen ? (
                <FeatureNotesPreviewDialog
                    open={isNotesPreviewOpen}
                    description={props.description}
                    onClose={onToggleNotesPreview}
                />
            ) : undefined}

            {isVideoModalOpen && props.infoUrl && (
                <VideoModal
                    open={isVideoModalOpen}
                    onClose={() => setIsVideoModalOpen(false)}
                    url={props.infoUrl}
                />
            )}

            <AnimatedItem
                noFill={true}
                className={classes.animatedFill}
                delay={Math.min(props.animationDelay || 0, MAX_ANIMATION_DELAY)}
            >
                <Card className={clsx(classes.card, !isVisible && classes.cardHidden)}>
                    <div className={classes.cardBody}>
                        <div className={classes.iconChip}>
                            <TrendingUpIcon />
                        </div>
                        <Typography className={classes.title}>
                            {props.title}
                        </Typography>
                        {!props.isDragDisabled && (
                            <DragIndicatorIcon className={classes.dragIndicator} />
                        )}
                    </div>
                    <div className={classes.cardFooter}>
                        <FormControlLabel
                            className={classes.visibilityLabel}
                            control={
                                <Switch
                                    size="small"
                                    checked={isVisible}
                                    onChange={onHiddenChange}
                                    color="primary"
                                />
                            }
                            label={isVisible ? 'Visible' : 'Hidden'}
                        />
                        <div className={classes.footerActions}>
                            {props.infoUrl && (
                                <Tooltip title="Watch video">
                                    <IconButton
                                        aria-label="learn more"
                                        size="small"
                                        className={classes.footerButton}
                                        onClick={() => setIsVideoModalOpen(true)}
                                    >
                                        <VideoLibraryOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            )}

                            {props.isInternalAdmin ? (
                                <Tooltip title="Edit notes">
                                    <IconButton
                                        aria-label="edit notes"
                                        size="small"
                                        className={classes.footerButton}
                                        onClick={() => setIsEditNotesOpen(true)}
                                    >
                                        <EditOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : null}

                            <Tooltip title="View notes">
                                <IconButton
                                    aria-label="view notes"
                                    size="small"
                                    className={classes.footerButton}
                                    onClick={onToggleNotesPreview}
                                >
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                </Card>
            </AnimatedItem>
        </React.Fragment>
    );
};

const FeatureLayoutCardContent = React.memo(FeatureLayoutCardContentComponent);

type FeatureLayoutCardDraggableProps = FeatureLayoutCardContentProps;

// Thin wrapper - only this part calls useSortable(), so only this part re-renders every drag
// frame. It stays cheap (just a div + hook), while the actual heavy content above is memoized
// and shielded from that per-frame churn.
const FeatureLayoutCardDraggableComponent: React.FC<FeatureLayoutCardDraggableProps> = props => {
    const classes = useFeatureLayoutCardStyles();

    // @dnd-kit/sortable's own hook - replaces react-beautiful-dnd's <Draggable> render-prop
    // (see FeatureLayoutConfigurationList.tsx's own comment on why the drag library changed).
    // transform/transition drive the live "siblings slide out of the way" animation as other
    // cards get dragged past this one; isDragging hides this card's own spot (opacity) while
    // FeatureLayoutConfigurationList.tsx's DragOverlay shows the floating copy instead.
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: props.featureGuid, disabled: props.isDragDisabled });

    const sortableStyle: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition ?? undefined,
        opacity: isDragging ? 0 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={sortableStyle}
            data-feature-guid={props.featureGuid}
            {...attributes}
            {...listeners}
            className={clsx(classes.cardSlot, props.isDragDisabled ? classes.pointerCursor : classes.grabCursor)}
        >
            <FeatureLayoutCardContent
                featureGuid={props.featureGuid}
                feature={props.feature}
                title={props.title}
                description={props.description}
                isHidden={props.isHidden}
                onIsHiddenUpdated={props.onIsHiddenUpdated}
                isDragDisabled={props.isDragDisabled}
                infoUrl={props.infoUrl}
                isInternalAdmin={props.isInternalAdmin}
                animationDelay={props.animationDelay}
            />
        </div>
    );
};

export const FeatureLayoutCardDraggable = FeatureLayoutCardDraggableComponent;
